import React from 'react';
import { BUTTONS } from '@buzunarul/shared-constants';
import { button, cn } from '../../../styles/cva-v2';
import Spinner from '../Spinner/Spinner';

export interface GoogleButtonProps {
  onClick: () => void;
  isLoading?: boolean;
  variant?: 'login' | 'register';
  className?: string;
  'data-testid'?: string;
}

// Simple Google icon SVG component
const GoogleIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
  >
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GoogleButton = ({
  onClick,
  isLoading = false,
  variant = 'login',
  className,
  'data-testid': dataTestId,
}: GoogleButtonProps) => {
  const buttonText = variant === 'login' 
    ? BUTTONS.LOGIN_WITH_GOOGLE 
    : BUTTONS.REGISTER_WITH_GOOGLE;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      className={cn(
        button({ 
          variant: "outline", 
          size: "md" 
        }),
        "w-full flex items-center justify-center gap-3",
        // Override outline variant to use carbon copper theme with Google branding
        "border-carbon-300 hover:border-copper-500",
        "bg-white hover:bg-copper-50",
        "text-carbon-700 hover:text-copper-600",
        "transition-all duration-200",
        "dark:border-carbon-600 dark:bg-carbon-900 dark:text-carbon-200",
        "dark:hover:border-copper-400 dark:hover:bg-copper-900/20",
        isLoading && "opacity-50 cursor-not-allowed",
        className
      )}
      data-testid={dataTestId}
    >
      {!isLoading && (
        <GoogleIcon className="w-5 h-5" />
      )}
      {isLoading ? (
        <div className="flex items-center gap-2">
          <Spinner size="sm" variant="default" />
          <span>{BUTTONS.LOADING}</span>
        </div>
      ) : (
        buttonText
      )}
    </button>
  );
};

export default GoogleButton;